import { CommonDialogs } from "@mcleod/common";
import {
    ClickEvent, DataDisplayEvent, DataSourceExecutionEvent, DropdownItem, Label, MapPin, Panel, Snackbar,
    Textbox
} from "@mcleod/components";
import { CityUtil, Clipboard, ErrorHandlerFunction, Navigation, StringUtil, UrlUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { PortalCompanySettings } from "@mcleod/portal-common";
import { AutogenLayoutTrackWithoutLogin } from "./autogen/AutogenLayoutTrackWithoutLogin";

export class TrackWithoutLogin extends AutogenLayoutTrackWithoutLogin {

    private stops: [];
    private stopsSet = new Set();

    override onLoad() {
        if (PortalCompanySettings.get().enable_unauth_tracking != true) {
            this.removeAll();
            CommonDialogs.showError("Unauthenticated tracking is not allowed.");
            return;
        }
        const searchItems = this.textboxSearchType.items as DropdownItem[];
        if (PortalCompanySettings.getSingleton().isBrokerageLtl() === true) {
            searchItems.push(new DropdownItem("CarrierProNum", "Carrier Pro #"));
            searchItems.push(new DropdownItem("ShipperPONum", "Shipper PO #"));
        }
        if (PortalCompanySettings.getSingleton().isAssetLtl() == true)
            searchItems.push(new DropdownItem("ProNum", "Pro #"));
        this.mapStops.immediatelyActivateMap = true;

        const urlProp = UrlUtil.getPropsFromUrl(document.location.search)
        if (urlProp != null && StringUtil.isEmptyString(urlProp.id)) {
            if (!StringUtil.isEmptyString(urlProp.search_type)) {
                searchItems.forEach(item => {
                    if (item.value == urlProp.search_type) {
                        this.textboxSearchType.selectedItem = item;
                        return;
                    }
                });
            }
            if (!StringUtil.isEmptyString(urlProp.search_value)) {
                this.textboxTrackingNumber.text = urlProp.search_value;
            }
            if (this.validateTextboxes()) {
                this.mainDataSource.search({ search_type: urlProp.search_type, search_value: urlProp.search_value }, this._errorHandler).then(response => {
                    this.checkResponse(response)
                    this.tabsetDetails.remove(this.tabMap);
                });
            }
            else {
                this.panelTrackAndTrace.visible = false;
                this.labelNoOrdersFound.visible = false;
            }
        }
        else if (urlProp != null && !StringUtil.isEmptyString(urlProp.id)) {
            this.mainDataSource.search({ token_id: urlProp.id }, this._errorHandler).then(response => this.checkResponse(response))
        }
        else {
            this.panelTrackAndTrace.visible = false;
            this.labelNoOrdersFound.visible = true;
        }
    }

    validateTextboxes(): boolean {
        let isValid = true;
        if (!this.textboxSearchType.validateSimple()) {
            this.textboxSearchType.showAdhocTooltip("Search Type Required");
            isValid = false;
        }
        if (!this.textboxTrackingNumber.validateSimple()) {
            this.textboxTrackingNumber.showAdhocTooltip("Tracking Number Required");
            isValid = false;
        }
        return isValid;
    }

    /** This is an event handler for the onDataDisplay event of textboxSchedArriveEarly.  */
    textboxSchedArriveEarlyOnDataDisplay(event: DataDisplayEvent) {
        const late = event.rowData.data["sched_arrive_late"];
        if (late != null) {
            (event.target as Textbox).caption = "Scheduled to arrive between";
        }
    }

    /** This is an event handler for the afterExecution event of sourceCustomerOrdersUnauthenticated.  */
    async sourceCustomerOrdersUnauthenticatedAfterExecution(event: DataSourceExecutionEvent) {
        if (event.dataSource.data[0]?.data?.error != null) {
            this.removeAll();
            CommonDialogs.showError(event.dataSource.data[0]?.data?.error);
            return;
        }

        const urlProp = UrlUtil.getPropsFromUrl(document.location.search);
        if (urlProp != null && !StringUtil.isEmptyString(urlProp.id)) {
            await this.sourceOrderStopsUnauthenticated.search({ token_id: urlProp.id });
        } else if (this.mainDataSource.activeRow?.getString("orders.id", null)) {
            await this.sourceOrderStopsUnauthenticated.search({ order_id: this.mainDataSource.activeRow?.getString("orders.id", null) });
        }

        const positions = event.dataSource.data[0]?.data?.positions;
        this.setUpMap(positions);
    }

    /** This is an event handler for the onDataDisplay event of labelStopNumber.  */
    labelStopNumberOnDataDisplay(event: DataDisplayEvent) {
        const status = event.rowData.data["status"];
        const statusLabel = (event.target as Label)
        if (status != null && status == "D") {
            statusLabel.text = null;
            statusLabel.padding = 0;
            statusLabel.imageName = ImageName.circleCheck;
            statusLabel.imageColor = "primary";
            statusLabel.imageWidth = 27;
            statusLabel.borderWidth = 0;

        }
        else if (event.rowData.data["actual_arrival"] != null && event.rowData.data["actual_departure"] == null) {
            statusLabel.backgroundColor = "primary";
            statusLabel.color = "primary.reverse"
        }
    }

    /** This is an event handler for the onDataDisplay event of panelLine.  */
    panelLineOnDataDisplay(event: DataDisplayEvent) {
        if (this.stops != null && event.rowData.data["order_sequence"] == this.stops.length) {
            (event.target as Panel).visible = false;
        }
    }

    /** This is an event handler for the onDataDisplay event of labelCityStateZip.  */
    labelCityStateZipOnDataDisplay(event: DataDisplayEvent) {
        (event.target as Label).text = CityUtil.formatCityStateZip(event.rowData?.get("city_name"), event.rowData?.get("state"), event.rowData?.get("zip_code"));
    }

    private setUpMap(positions: any) {
        if (positions != null) {
            for (let x = positions.length - 1; x > -1; x--) {
                this.addPositionPin(positions[x]);
            }
        }
    }

    private addPositionPin(position: any) {
        const pin = new MapPin(position.latitude, position.longitude);

        if (position.image != null)
            pin.image = position.image;
        else
            pin.image = "map-pin-white-dot-large";

        pin.tooltip = position.descr;
        pin.caption = position.sequence;
        if (position.caption_color != null)
            pin.captionColor = position.caption_color;
        else
            pin.captionColor = "Blue";
        pin.layoutName = null;

        const key = position.latitude + position.longitude;
        const descr = position.descr;
        if (descr != null && descr.startsWith("Stop")) {
            if (!this.stopsSet.has(key)) {
                this.mapStops.addPin(pin);
                this.stopsSet.add(key);
            }
        }
        else {
            pin.clusterable = false;
            this.mapStops.addPin(pin);
        }
    }

    private checkResponse(response: any) {
        if (response == null || response.modelRows.length == 0) {
            this.panelTrackAndTrace.visible = false;
            this.labelNoOrdersFound.visible = true;
        }
        else {
            this.panelTrackAndTrace.visible = true;
            this.labelNoOrdersFound.visible = false;
        }
    }

    async buttonCopyLinkOnClick() {
        await Clipboard.copyText(window.location.href);
        Snackbar.showSnackbar("A link to this order has been copied to your clipboard.");
    }

    _errorHandler: ErrorHandlerFunction = (error) => {
        Navigation.navigateTo("");
    }
}
